import { escapeHtml, formatCaptureTime } from '../editnote/exhibit-photos/exhibit-set-html';
import { buildLegend, EvidenceMarker } from './evidence-markers';
import { PointerKind } from './ink';
import { describeScale, isScaleCalibration, ScaleCalibration } from './measure';
import { PAPER_LABELS, PaperKind } from './paper';
import { SketchKind } from './sketch-tools';

export const SKETCH_SCHEMA_VERSION = 1;
export const SKETCH_TOOL_NAME = 'Forensic Notes Sketch Pad';
export const SKETCH_ROOT_CLASS = 'fn-sketch';
export const SKETCH_MARKER_ATTR = 'data-fn-sketch';
export const SKETCH_REVISION_ATTR = 'data-fn-sketch-rev';
export const SKETCH_TITLE_ATTR = 'data-fn-sketch-title';
export const SKETCH_DOC_ATTR = 'data-fn-sketch-doc';
export const SKETCH_IMAGE_CLASS = 'fn-sketch-image';
export const SKETCH_SOURCE_CLASS = 'fn-sketch-source';
export const SKETCH_SOURCE_DISPLAY_WIDTH_PX = 220;
export const NUMBER_DECIMALS = 2;
export const REVISION_ID_SHORT_LENGTH = 8;
export const DEFAULT_SKETCH_TITLE = 'Untitled sketch';
export const MAX_SKETCH_OBJECTS = 5000;
export const MAX_PAGE_EDGE_PX = 10000;
export const IMAGE_DATA_URL_PATTERN = /^data:image\/[a-z0-9+.-]+;base64,[A-Za-z0-9+/=]+$/i;

export enum SketchBackgroundKind {
  None = 'none',
  Photo = 'photo',
  Template = 'template',
}

export enum RevisionBase {
  None = 'none',
  Sketch = 'sketch',
  Image = 'image',
}

export interface ExportedImageInfo {
  readonly width: number;
  readonly height: number;
  readonly multiplier: number;
}

export const NOT_EXPORTED: ExportedImageInfo = { width: 0, height: 0, multiplier: 0 };

export interface SketchBackground {
  readonly kind: SketchBackgroundKind;
  readonly width: number;
  readonly height: number;
  readonly templateTitle: string;
}

export interface SketchMeta {
  readonly title: string;
  readonly description: string;
  readonly paper: PaperKind;
  readonly pageWidth: number;
  readonly pageHeight: number;
  readonly scale: ScaleCalibration | null;
  readonly markers: readonly EvidenceMarker[];
  readonly markerCounter: number;
  readonly createdAtIso: string;
  readonly savedAtIso: string;
  readonly revisionId: string;
  readonly revisionOfNoteId: string | null;
  readonly revisionBase: RevisionBase;
  readonly inputs: readonly PointerKind[];
  readonly background: SketchBackground;
  readonly objectCount: number;
  readonly image: ExportedImageInfo;
}

export interface SketchDocument {
  readonly schema: number;
  readonly tool: string;
  readonly meta: SketchMeta;
  readonly objects: readonly unknown[];
}

export interface SketchNoteRender {
  readonly doc: SketchDocument;
  readonly imageDataUrl: string;
  readonly sourcePhotoDataUrl: string | null;
  readonly timeZone: string;
}

const INPUT_LABELS: Readonly<Record<PointerKind, string>> = {
  pen: 'Stylus (pressure where the device reports it)',
  touch: 'Finger',
  mouse: 'Mouse or trackpad',
};

const PAPER_KINDS: readonly PaperKind[] = [PaperKind.Blank, PaperKind.Grid, PaperKind.Lined, PaperKind.Dots, PaperKind.Photo, PaperKind.Template];
const BACKGROUND_KINDS: readonly SketchBackgroundKind[] = [SketchBackgroundKind.None, SketchBackgroundKind.Photo, SketchBackgroundKind.Template];
const POINTER_KINDS: readonly PointerKind[] = ['pen', 'touch', 'mouse'];
const REVISION_BASES: readonly RevisionBase[] = [RevisionBase.None, RevisionBase.Sketch, RevisionBase.Image];

export function roundNumbersDeep(value: unknown, decimals: number = NUMBER_DECIMALS): unknown {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return value;
    }
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }
  if (Array.isArray(value)) {
    return value.map((v) => roundNumbersDeep(v, decimals));
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = roundNumbersDeep(v, decimals);
    }
    return out;
  }
  return value;
}

export function compactObjects(objects: readonly unknown[]): unknown[] {
  return objects
    .filter((o) => {
      const kind = o && typeof o === 'object' ? (o as Record<string, unknown>)['fnKind'] : undefined;
      return kind !== SketchKind.Page && kind !== SketchKind.Background;
    })
    .map((o) => {
      const obj = { ...(o as Record<string, unknown>) };
      const kind = obj['fnKind'];
      if ((kind === SketchKind.Ink || kind === SketchKind.Highlight) && Array.isArray(obj['fnInk'])) {
        delete obj['path'];
      }
      return roundNumbersDeep(obj);
    });
}

export function slugify(text: string): string {
  const slug = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return slug || 'sketch';
}

export function sketchFileName(title: string, revisionId: string, suffix: string, extension: string): string {
  return `Sketch-${slugify(title)}-${revisionId.slice(0, REVISION_ID_SHORT_LENGTH)}${suffix}.${extension}`;
}

export function describeInputs(inputs: readonly PointerKind[]): string {
  const labels = POINTER_KINDS.filter((k) => inputs.includes(k)).map((k) => INPUT_LABELS[k]);
  return labels.length > 0 ? labels.join('; ') : 'Not recorded';
}

export function describeBackground(meta: SketchMeta): string {
  switch (meta.background.kind) {
    case SketchBackgroundKind.Photo:
      return `Drawn over a photo (${meta.background.width} x ${meta.background.height} px). The unmarked source photo is attached below.`;
    case SketchBackgroundKind.Template:
      return `Drawn on the "${meta.background.templateTitle}" template (${meta.background.width} x ${meta.background.height} px). The unmarked template is attached below.`;
    default:
      return PAPER_LABELS[meta.paper] || PAPER_LABELS[PaperKind.Blank];
  }
}

export function describePage(meta: SketchMeta): string {
  const page = `${meta.pageWidth} x ${meta.pageHeight} px on the page.`;
  if (meta.image.width > 0 && meta.image.height > 0) {
    return `${page} The attached image is ${meta.image.width} x ${meta.image.height} px (${meta.image.multiplier.toFixed(2)} x the page).`;
  }
  return page;
}

export function describeVersion(meta: SketchMeta): string {
  switch (meta.revisionBase) {
    case RevisionBase.Sketch:
      return 'Revision of an earlier version of this sketch. Earlier versions are kept in this note\'s history.';
    case RevisionBase.Image:
      return 'Marked-up version of an earlier image note. The earlier version is kept in this note\'s history.';
    default:
      return 'First version of this sketch.';
  }
}

export const MARKER_NUMBERING_NOTE = 'Marker numbers are assigned in the order the markers were placed and are never reused; a missing number means that marker was removed before this version was saved.';

function sourceTitle(kind: SketchBackgroundKind): string {
  return kind === SketchBackgroundKind.Template ? 'Template, unmarked' : 'Source photo, unmarked';
}

function sourceSuffix(kind: SketchBackgroundKind): string {
  return kind === SketchBackgroundKind.Template ? '-template' : '-source';
}

function metaRow(label: string, value: string): string {
  return `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`;
}

function legendRows(markers: readonly EvidenceMarker[]): string {
  return buildLegend(markers)
    .map((m) => `<tr><td class="fn-sketch-legend-num">${m.number}</td><td>${escapeHtml(m.label || '(no description)')}</td></tr>`)
    .join('');
}

export function dataUrlExtension(dataUrl: string): string {
  const match = /^data:image\/([a-z0-9+.-]+);base64,/i.exec(dataUrl);
  if (!match) {
    return 'png';
  }
  const type = match[1].toLowerCase();
  return type === 'jpeg' ? 'jpg' : type;
}

export function renderSketchNoteHtml(input: SketchNoteRender): string {
  const { doc, imageDataUrl, sourcePhotoDataUrl, timeZone } = input;
  const meta = doc.meta;
  const title = meta.title.trim() || DEFAULT_SKETCH_TITLE;
  const legend = buildLegend(meta.markers);
  const docJson = escapeHtml(JSON.stringify(doc));
  const imageName = sketchFileName(title, meta.revisionId, '', dataUrlExtension(imageDataUrl));

  const metaRows = [
    metaRow('Started', formatCaptureTime(meta.createdAtIso, timeZone)),
    metaRow('Saved', `${formatCaptureTime(meta.savedAtIso, timeZone)} (submitted for timestamping; the timestamp itself is recorded by the server)`),
    metaRow('Drawn on', describeBackground(meta)),
    metaRow('Page', describePage(meta)),
    metaRow('Scale', describeScale(meta.scale, meta.image.multiplier, meta.background.kind === SketchBackgroundKind.Photo)),
    metaRow('Input used', describeInputs(meta.inputs)),
    metaRow('Version', describeVersion(meta)),
  ].join('');

  const legendHtml =
    legend.length > 0
      ? `<p class="fn-sketch-legend-title"><strong>Evidence markers</strong></p>` +
        `<table class="fn-sketch-legend"><thead><tr><th>#</th><th>Description</th></tr></thead><tbody>${legendRows(legend)}</tbody></table>` +
        `<p class="fn-sketch-legend-note">${MARKER_NUMBERING_NOTE}</p>`
      : '';

  const sourceHtml = sourcePhotoDataUrl
    ? `<p class="fn-sketch-source-title">${sourceTitle(meta.background.kind)}</p>` +
      `<img class="${SKETCH_SOURCE_CLASS}" src="${sourcePhotoDataUrl}" alt="${escapeHtml(sourceTitle(meta.background.kind))}" ` +
      `filename="${escapeHtml(sketchFileName(title, meta.revisionId, sourceSuffix(meta.background.kind), dataUrlExtension(sourcePhotoDataUrl)))}" ` +
      `style="max-width: ${SKETCH_SOURCE_DISPLAY_WIDTH_PX}px" />`
    : '';

  return (
    `<div class="${SKETCH_ROOT_CLASS}" ${SKETCH_MARKER_ATTR}="${SKETCH_SCHEMA_VERSION}" ` +
    `${SKETCH_REVISION_ATTR}="${escapeHtml(meta.revisionId)}" ${SKETCH_TITLE_ATTR}="${escapeHtml(title)}" ` +
    `${SKETCH_DOC_ATTR}="${docJson}">` +
    `<p class="fn-sketch-title"><strong>Sketch: ${escapeHtml(title)}</strong></p>` +
    (meta.description.trim() ? `<p class="fn-sketch-description">${escapeHtml(meta.description.trim())}</p>` : '') +
    `<img class="${SKETCH_IMAGE_CLASS}" src="${imageDataUrl}" alt="${escapeHtml('Sketch: ' + title)}" ` +
    `filename="${escapeHtml(imageName)}" style="max-width: 100%" />` +
    `<table class="fn-sketch-meta"><tbody>${metaRows}</tbody></table>` +
    legendHtml +
    sourceHtml +
    `<p class="fn-sketch-footer">Drawn in ${SKETCH_TOOL_NAME}. The image is the record. ` +
    `The drawing data embedded in this note lets the sketch be reopened and revised as a new version.</p>` +
    `</div>`
  );
}

const ROOT_TAG_PATTERN = new RegExp(`<div class="${SKETCH_ROOT_CLASS}" ${SKETCH_MARKER_ATTR}="(\\d+)"[^>]*>`);

export function isSketchHtml(html: string | null | undefined): boolean {
  return typeof html === 'string' && ROOT_TAG_PATTERN.test(html);
}

function unescapeHtml(text: string): string {
  return text
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

function readAttr(tag: string, name: string): string | null {
  const match = new RegExp(`${name}="([^"]*)"`).exec(tag);
  return match ? unescapeHtml(match[1]) : null;
}

function isExportedImageInfo(value: unknown): value is ExportedImageInfo {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    typeof v['width'] === 'number' &&
    v['width'] >= 0 &&
    typeof v['height'] === 'number' &&
    v['height'] >= 0 &&
    typeof v['multiplier'] === 'number' &&
    v['multiplier'] >= 0
  );
}

export function isSketchMeta(value: unknown): value is SketchMeta {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const m = value as Record<string, unknown>;
  const background = m['background'] as Record<string, unknown> | undefined;
  return (
    typeof m['title'] === 'string' &&
    typeof m['description'] === 'string' &&
    PAPER_KINDS.includes(m['paper'] as PaperKind) &&
    typeof m['pageWidth'] === 'number' &&
    m['pageWidth'] > 0 &&
    m['pageWidth'] <= MAX_PAGE_EDGE_PX &&
    typeof m['pageHeight'] === 'number' &&
    m['pageHeight'] > 0 &&
    m['pageHeight'] <= MAX_PAGE_EDGE_PX &&
    (m['scale'] === null || isScaleCalibration(m['scale'])) &&
    Array.isArray(m['markers']) &&
    typeof m['markerCounter'] === 'number' &&
    Number.isInteger(m['markerCounter']) &&
    (m['markerCounter'] as number) >= 0 &&
    typeof m['createdAtIso'] === 'string' &&
    typeof m['savedAtIso'] === 'string' &&
    typeof m['revisionId'] === 'string' &&
    m['revisionId'].length > 0 &&
    (m['revisionOfNoteId'] === null || typeof m['revisionOfNoteId'] === 'string') &&
    REVISION_BASES.includes(m['revisionBase'] as RevisionBase) &&
    isExportedImageInfo(m['image']) &&
    Array.isArray(m['inputs']) &&
    (m['inputs'] as unknown[]).every((i) => POINTER_KINDS.includes(i as PointerKind)) &&
    !!background &&
    BACKGROUND_KINDS.includes(background['kind'] as SketchBackgroundKind) &&
    typeof background['width'] === 'number' &&
    typeof background['height'] === 'number' &&
    typeof background['templateTitle'] === 'string' &&
    typeof m['objectCount'] === 'number'
  );
}

export function isSketchDocument(value: unknown): value is SketchDocument {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const d = value as Record<string, unknown>;
  return (
    d['schema'] === SKETCH_SCHEMA_VERSION &&
    typeof d['tool'] === 'string' &&
    isSketchMeta(d['meta']) &&
    Array.isArray(d['objects']) &&
    (d['objects'] as unknown[]).length <= MAX_SKETCH_OBJECTS
  );
}

export function isImageDataUrl(value: string | null | undefined): boolean {
  return typeof value === 'string' && IMAGE_DATA_URL_PATTERN.test(value);
}

export function inlineImageSource(html: string | null | undefined, cssClass: string): string | null {
  if (typeof html !== 'string') {
    return null;
  }
  const imgPattern = /<img\b[^>]*>/gi;
  let match: RegExpExecArray | null;
  while ((match = imgPattern.exec(html)) !== null) {
    const tag = match[0];
    const classAttr = readAttr(tag, 'class') || '';
    if (classAttr.split(/\s+/).includes(cssClass)) {
      const src = readAttr(tag, 'src');
      return isImageDataUrl(src) ? src : null;
    }
  }
  return null;
}

export function parseSketchDocument(html: string | null | undefined): SketchDocument | null {
  if (typeof html !== 'string') {
    return null;
  }
  const tagMatch = ROOT_TAG_PATTERN.exec(html);
  if (!tagMatch) {
    return null;
  }
  const docJson = readAttr(tagMatch[0], SKETCH_DOC_ATTR);
  if (docJson === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(docJson);
  } catch {
    return null;
  }
  return isSketchDocument(parsed) ? parsed : null;
}

export function sketchTitleOf(html: string | null | undefined): string | null {
  if (typeof html !== 'string') {
    return null;
  }
  const tagMatch = ROOT_TAG_PATTERN.exec(html);
  return tagMatch ? readAttr(tagMatch[0], SKETCH_TITLE_ATTR) : null;
}

export function firstAttachedImageFileId(html: string | null | undefined, cssClass: string): string | null {
  if (typeof html !== 'string') {
    return null;
  }
  const imgPattern = /<img\b[^>]*>/gi;
  let match: RegExpExecArray | null;
  while ((match = imgPattern.exec(html)) !== null) {
    const tag = match[0];
    const classAttr = readAttr(tag, 'class') || '';
    if (classAttr.split(/\s+/).includes(cssClass)) {
      return readAttr(tag, 'data-fileid');
    }
  }
  return null;
}
